// The Retail/SuperMarket till tools a Fashion till also offers (cash in / out,
// gift cards, coupons, carry bags, notes, estimates), each behind
// the same till access or POS config switch the SuperMarket till reads, so a
// store turns them on and off in one place for every layout.
const yes = (v) => String(v ?? "").trim().toUpperCase() === "Y";

// Which tools this till shows. tillAccess flags come from the till's access
// record; the rest from cwr_pos_config.
export function fashionTools({ tillData, posConfig } = {}) {
  const access = tillData?.tillAccess || {};
  const cash = yes(access.cashManagement);
  return {
    cash,
    addCash: cash && yes(posConfig?.enableAddCash),
    removeCash: cash && yes(posConfig?.enableRemoveCash),
    giftCard: yes(access.giftCard),
    bags: yes(posConfig?.showBags),
    lineNotes: yes(posConfig?.enableLineLevelNarration),
    estimates: yes(posConfig?.showEstimates),
  };
}

// Why cash in / out can't start now, or null. It's a till movement, not a
// sale, so the bill must be empty, and the server records it straight away.
export function cashBlockedReason({ isOnline, cart }) {
  if (isOnline === false) return "Cash in / out needs a connection. Reconnect the till and try again.";
  if (cart?.items?.length > 0) return "Finish or park the open bill first. Cash in / out is kept apart from sales.";
  return null;
}

// Why a coupon can't be applied now, or null.
export function couponBlockedReason(cart) {
  if (!cart?.items?.length) return "Add items to the bill first, then apply the coupon.";
  if (cart.items.some((i) => i.isReturn)) return "Coupons can't be applied to a return.";
  return null;
}

// How a gift card sale starts: "invoice" sells it as a line on this bill;
// "topup" issues or tops up the customer's card, which needs a named customer
// with a mobile number. Returns { mode } or { error }.
export function giftCardStart({ posConfig, cart, tillData, isOnline }) {
  if (isOnline === false) return { error: "Gift cards need a connection. Reconnect the till and try again." };
  if (yes(posConfig?.showGiftCardSaleInInvoice)) return { mode: "invoice" };
  const walkIn = tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId;
  const customer = cart?.customer;
  if (!customer?.cwrCustomerId || customer.cwrCustomerId === walkIn || !customer.mobileNo) {
    return { error: "Add the customer (with a mobile number) to the bill first. The gift card is issued to them." };
  }
  return { mode: "topup" };
}

const CASH_TYPES = {
  cashOut: { label: "Cash out", out: true },
  pettyCashOut: { label: "Petty cash out", out: true },
  pettyCashIn: { label: "Petty cash in", out: false },
  bankTransfer: { label: "Bank transfer", out: true },
};

// A cash in / out entry as the panel shows it: label and signed amount.
export function cashEntry(entry) {
  const t = CASH_TYPES[entry?.type] || { label: "Cash in", out: false };
  const amount = Number(entry?.amount) || 0;
  return { label: t.label, out: t.out, signed: t.out ? -amount : amount };
}

// Net cash moved by these entries (in minus out).
export const cashNet = (entries) => (entries || []).reduce((n, e) => n + cashEntry(e).signed, 0);

// Whether the cashier may key a price for this line. Only open-price items
// (product master "POS price edit" = Y, the same flag the SuperMarket till
// reads) take a price at the till; a markdown on any other item goes through
// Line discount, which has its rules and approvals.
export const canChangePrice = (line) => Boolean(line) && yes(line.posPriceEdit) && !line.isReturn && Number(line.weight) > 0;

// A new unit price for an open-price line, checked: { price } or { error }.
export function checkPriceChange(line, raw, precision = 2) {
  const text = String(raw ?? "").trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return { error: "Enter the price as a number, like 799 or 799.50." };
  const price = Number(text);
  if (price <= 0) return { error: "The price must be more than zero." };
  const decimals = text.split(".")[1]?.length || 0;
  if (decimals > precision) return { error: `Use at most ${precision} decimal places.` };
  return { price };
}
